import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Calendar,
  Clock,
  MapPin,
  Trophy,
  Loader2,
  CalendarX,
  ChevronRight,
  IndianRupee
} from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';

import { BACKEND_URL, API } from '../../utils/apiConfig';

export default function MyBookingsPage() {
  const { isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [isAuthenticated, activeTab, navigate]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${API}/user/bookings`, {
        params: { booking_status: activeTab },
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateStr === today.toISOString().split('T')[0]) {
      return 'Today';
    } else if (dateStr === tomorrow.toISOString().split('T')[0]) {
      return 'Tomorrow';
    }
    return format(date, 'EEE, d MMM');
  };

  const formatFullDate = (dateStr) => {
    return format(new Date(dateStr), 'EEEE, d MMMM yyyy');
  };

  // Group bookings by date
  const groupedBookings = bookings.reduce((groups, booking) => {
    const date = booking.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(booking);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedBookings).sort((a, b) => {
    return activeTab === 'upcoming' 
      ? new Date(a) - new Date(b)
      : new Date(b) - new Date(a);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white px-4 py-4">
        <h1 className="text-xl font-bold">My Bookings</h1>
        <p className="text-purple-200 text-sm">View and manage your court reservations</p>
      </div>

      {/* Tabs */}
      <div className="bg-white/80 backdrop-blur border-b border-purple-100 px-4 py-3 sticky top-[52px] z-10">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'upcoming'
                ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/25'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="upcoming-tab"
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'past'
                ? 'bg-gradient-to-r from-slate-500 to-slate-600 text-white shadow-lg'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="past-tab"
          >
            Past
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-purple-100 shadow-sm">
            <CalendarX className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">
              No {activeTab} bookings
            </h3>
            <p className="text-sm text-slate-500 mb-6">
              {activeTab === 'upcoming' 
                ? "You don't have any upcoming bookings yet"
                : "You don't have any past bookings"}
            </p>
            {activeTab === 'upcoming' && (
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold shadow-lg shadow-purple-500/25 hover:from-violet-600 hover:to-purple-700 transition-all"
              >
                Book a Court Now
                <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDates.map(date => (
              <div key={date}>
                <h3 className="text-sm font-bold text-slate-500 mb-3 flex items-center gap-2 uppercase tracking-wide">
                  <Calendar className="w-4 h-4 text-violet-500" />
                  {formatDate(date)}
                </h3>
                <div className="space-y-3">
                  {groupedBookings[date].map(booking => (
                    <div
                      key={booking.booking_id}
                      className="bg-white rounded-2xl border border-purple-100 shadow-md overflow-hidden"
                      data-testid={`booking-card-${booking.booking_id}`}
                    >
                      {/* Status Bar */}
                      <div className={`h-1.5 ${
                        activeTab === 'upcoming' 
                          ? 'bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500'
                          : 'bg-gradient-to-r from-slate-400 to-slate-500'
                      }`} />
                      
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              activeTab === 'upcoming'
                                ? 'bg-gradient-to-br from-violet-100 to-purple-100'
                                : 'bg-slate-100'
                            }`}>
                              <Trophy className={`w-6 h-6 ${
                                activeTab === 'upcoming'
                                  ? 'text-violet-600'
                                  : 'text-slate-500'
                              }`} />
                            </div>
                            <div>
                              <p className="font-bold text-slate-900">{booking.game}</p>
                              <p className="text-xs text-violet-600 font-medium">{booking.court}</p>
                            </div>
                          </div>
                          <span className={`text-xs px-3 py-1.5 rounded-full font-semibold ${
                            booking.status === 'Confirmed'
                              ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                              : booking.status === 'Completed'
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-red-50 text-red-600 border border-red-200'
                          }`}>
                            {booking.status}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-slate-600">
                            <MapPin className="w-4 h-4 text-pink-500" />
                            <span className="truncate">{booking.venue}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="w-4 h-4 text-cyan-500" />
                            <span>{booking.start_time} - {booking.end_time}</span>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-purple-100 flex items-center justify-between">
                          <span className="text-xs text-slate-500">
                            {formatFullDate(booking.date)}
                          </span>
                          <div className="flex items-center gap-3">
                            {booking.amount > 0 && (
                              <span className="text-sm font-semibold text-emerald-600 flex items-center">
                                <IndianRupee className="w-3.5 h-3.5" />
                                {booking.amount}
                              </span>
                            )}
                            <span className="text-xs font-mono text-slate-400 bg-slate-50 px-2 py-1 rounded">
                              {booking.booking_id}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      {activeTab === 'upcoming' && bookings.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-purple-100 p-4 shadow-2xl">
          <Link
            to="/"
            className="block w-full py-3.5 text-center bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white font-semibold rounded-xl shadow-lg shadow-purple-500/25"
            data-testid="book-more-btn"
          >
            Book Another Slot
          </Link>
        </div>
      )}
      
      {/* Footer */}
      <CustomerFooter />
    </div>
  );
}
